// AnalyticsWheelThumb — the "Your Wheels" row thumbnail. Fetches the row's own
// wheel segments (useWheelSegments; the analytics endpoint carries none) and
// renders them through the SAME shared D3 wheel renderer gallery/discover/
// collection cards use (WheelComponent), exactly like CollectionCard's
// showcased-wheel art. Falls back to the existing WheelThumb (portrait or the
// generic logo) while loading or when a wheel genuinely has no segments —
// never a hand-rolled renderer.
import React, { useMemo } from "react";
import styled from "styled-components";
import WheelComponent from "../WheelComponent";
import WheelThumb from "./WheelThumb";
import useWheelSegments from "../../services/useWheelSegments";
import { ASSETS } from "../../utils/config";
import { withPaletteResolvedColors } from "../SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteFromFill";
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";

const NOOP = () => {};

const ArtFrame = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  overflow: hidden;

  & svg {
    display: block;
  }
`;

const AnalyticsWheelThumb = ({ wheelId, portrait, name, size = 40 }) => {
  const { segments } = useWheelSegments(wheelId);

  const sortedSegments = useMemo(() => {
    const segs = Array.isArray(segments) ? segments : [];
    const sorted = [...segs].sort((a, b) => a.position - b.position);
    return withPaletteResolvedColors(sorted, {
      enabled: SEGMENT_PALETTE_COLOR_ENABLED,
    });
  }, [segments]);

  const hasWheelArt = sortedSegments.length > 0;

  const wheelArtProps = useMemo(
    () => ({
      userData: {
        id: String(wheelId ?? "analytics-row"),
        url: portrait || ASSETS.wheelLogoFallback,
        name,
      },
      maxSelectedArcs: 0,
      arcThickness: -10,
      chartData: sortedSegments,
      margin: 0,
      setChartData: NOOP,
      width: "100%",
      height: "100%",
      containerDivStyle: { width: "100%", height: "100%" },
      resize: true,
    }),
    [wheelId, portrait, name, sortedSegments]
  );

  if (!hasWheelArt) {
    return <WheelThumb portrait={portrait} name={name} size={size} />;
  }

  return (
    <ArtFrame
      $size={size}
      data-testid="analytics-wheel-art"
      role="img"
      aria-label={name ? `${name} Wheel` : "iYuga Wheel"}
    >
      <WheelComponent {...wheelArtProps} />
    </ArtFrame>
  );
};

export default AnalyticsWheelThumb;
